import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useNote } from '@/hooks/use-api'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { formatDate } from '@/lib/utils'
import { FileText, Lock, Building2, Edit2, Trash2 } from 'lucide-react'
import type { Note } from '@/types'

interface NoteDetailModalProps {
  noteId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  currentUserId?: string
  currentUserType?: string
  onEdit?: (note: Note) => void
  onDelete?: (note: Note) => void
}

export function NoteDetailModal({
  noteId,
  open,
  onOpenChange,
  currentUserId,
  currentUserType,
  onEdit,
  onDelete,
}: NoteDetailModalProps) {
  const { data, isLoading, error } = useNote(noteId || '')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const note = data

  const canEdit =
    note &&
    currentUserId &&
    currentUserType &&
    (note.owner_id === currentUserId || ['Admin', 'Super Admin'].includes(currentUserType))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[80vh] overflow-y-auto"
        aria-describedby="note-description"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" aria-hidden="true" />
            <span>{note?.title || 'Note Details'}</span>
            {note?.is_private && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <Lock className="h-3 w-3" aria-hidden="true" />
                Private
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div id="note-description" className="space-y-6">
          {isLoading && (
            <div
              className="text-center py-8 text-muted-foreground"
              role="status"
              aria-live="polite"
            >
              <div
                className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-current border-r-transparent mb-2"
                role="progressbar"
                aria-label="Loading note details"
              ></div>
              <p>Loading note details...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-8 text-destructive" role="alert">
              Error loading note: {error.message}
            </div>
          )}

          {note && (
            <>
              {/* Note Information */}
              <section>
                <h3 className="text-lg font-semibold mb-3 bg-muted px-3 py-1.5 rounded-sm">
                  Note Information
                </h3>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Owner</dt>
                    <dd className="mt-1 text-sm">{note.owner_name || 'Unknown'}</dd>
                  </div>

                  {note.account_name && (
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                        <Building2 className="h-4 w-4" aria-hidden="true" />
                        Account
                      </dt>
                      <dd className="mt-1 text-sm">
                        {note.account_id && note.account_name ? (
                          <Link
                            to={`/accounts/${note.account_id}`}
                            onClick={() => onOpenChange(false)}
                            className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 font-medium"
                            aria-label={`View account: ${note.account_name}`}
                          >
                            {note.account_name}
                          </Link>
                        ) : (
                          <span>{note.account_name}</span>
                        )}
                      </dd>
                    </div>
                  )}

                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Created Date</dt>
                    <dd className="mt-1 text-sm">
                      {note.created_date ? formatDate(note.created_date) : '-'}
                    </dd>
                  </div>

                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Last Modified</dt>
                    <dd className="mt-1 text-sm">
                      {note.last_modified_date ? formatDate(note.last_modified_date) : '-'}
                    </dd>
                  </div>
                </dl>
              </section>

              {/* Note Content */}
              <section>
                <h3 className="text-lg font-semibold mb-3 bg-muted px-3 py-1.5 rounded-sm">
                  Content
                </h3>
                <div className="bg-muted/30 rounded-lg p-4 border">
                  {note.body ? (
                    <p className="text-sm whitespace-pre-wrap">{note.body}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No content</p>
                  )}
                </div>
              </section>

              {canEdit && (
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onEdit?.(note)
                      onOpenChange(false)
                    }}
                  >
                    <Edit2 className="h-4 w-4 mr-2" aria-hidden="true" />
                    Edit
                  </Button>

                  <AlertDialog open={showDeleteConfirm}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />
                      Delete
                    </Button>

                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete note?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="flex justify-end gap-2">
                        <AlertDialogCancel onClick={() => setShowDeleteConfirm(false)}>
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => {
                            onDelete?.(note)
                            setShowDeleteConfirm(false)
                            onOpenChange(false)
                          }}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </div>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
